import type {
  EnemyTypeId,
  LevelDef,
  RegionDef,
  StationSpec,
  WaveDef,
  WaveGroup,
} from '@playnets/sim';
import { REGIONS } from './regions';

/* Authoring helpers. Waves are data; these just keep the data readable. */

const deg = (d: number): number => (d * Math.PI) / 180;

/** Screen bearings. 0 = east, 90 = south, 180 = west, 270 = north. */
export const N = deg(270);
export const NE = deg(315);
export const E = deg(0);
export const SE = deg(45);
export const S = deg(90);
export const SW = deg(135);
export const W = deg(180);
export const NW = deg(225);

interface GroupOpts {
  count?: number;
  fan?: number;
  at?: number;
  gap?: number;
  stage?: number;
}

function g(enemy: string, bearing: number | 'random', opts: GroupOpts = {}): WaveGroup {
  return {
    enemy,
    bearing,
    count: opts.count ?? 1,
    fan: opts.fan ?? 0,
    at: opts.at ?? 0,
    gap: opts.gap ?? 0.45,
    ...(opts.stage !== undefined ? { stage: opts.stage } : {}),
  };
}

/**
 * Every wave is untimed in prep (`prep: 0`). You build, you look at what you
 * built, and you press the button when you're ready. Nothing should punish a
 * player for thinking during the phase that exists for thinking — all the time
 * pressure belongs inside the wave.
 */
function wave(id: string, reward: number, groups: WaveGroup[]): WaveDef {
  return { id, prep: 0, reward, groups };
}

/**
 * Artificial stations, placed by bearing.
 *
 * `stations(N, S)` parks one due north and one due south at the default
 * radius; `stations({ bearing: E, r: 280 })` pushes one further out. Both forms
 * mix freely in the same call, so a level can say exactly where its platforms
 * are with one line.
 */
export function stations(
  ...at: Array<number | StationSpec>
): readonly StationSpec[] {
  return at.map((a) => (typeof a === 'number' ? { bearing: a } : a));
}

interface LevelExtras {
  structures?: readonly string[];
  mounts?: { surface?: number; ring?: number };
  stations?: readonly StationSpec[];
  upgrades?: readonly string[];
  startingUpgrades?: Record<string, number>;
  requireBuild?: boolean;
}

function level(
  id: string,
  name: string,
  kind: LevelDef['kind'],
  region: string,
  startingJuice: number,
  waves: WaveDef[],
  next: string[],
  modifiers: string[] = [],
  extras: LevelExtras = {},
): LevelDef {
  return { id, name, kind, region, modifiers, startingJuice, waves, next, ...extras };
}

/* ------------------------------------------------------------------ *
 * The Inner Reach — Terra's home, hand-authored.
 *
 * Levels 1–4 introduce exactly one system each (risk #4): a single bearing,
 * then dual-corridor entry, then the ultimate, then burrowers. Nothing new
 * arrives until the last thing has been used in anger.
 * ------------------------------------------------------------------ */

const R = 'inner-reach';

const innerReach: LevelDef[] = [
  // The opening level is a tutorial and is built like one: two waves, one thing
  // to build, drones only, and the juice magnet turned right up so a new player
  // isn't fighting the collection mechanic while learning everything else.
  level(
    'inner-reach-1',
    'First Light',
    'standard',
    R,
    // Enough for all three mounts with change to spare. A level that shows you
    // three sockets and hands you money for two is telling you to leave a hole
    // in your own board on the one level that exists to teach you not to.
    26,
    [
      wave('w1', 3, [g('drone', S, { count: 5, fan: deg(26), gap: 0.6 })]),
      wave('w2', 5, [
        g('drone', N, { count: 6, fan: deg(34), gap: 0.5 }),
        g('drone', SE, { count: 4, fan: deg(24), at: 4, gap: 0.5 }),
      ]),
    ],
    ['inner-reach-2'],
    [],
    // Three sockets, all of them in orbit, one gun, one planet power. The ring
    // is the layer this game is actually about — a sweeping arc rather than a
    // fixed one — so it's what the first level teaches, and the orbit parks
    // itself during prep so mounting isn't a moving-target problem.
    {
      structures: ['satellite'],
      upgrades: ['hull'],
      mounts: { surface: 0, ring: 3 },
      startingUpgrades: { magnet: 3 },
      requireBuild: true,
    },
  ),
  level(
    'inner-reach-2',
    'Two Streams',
    'standard',
    R,
    26,
    [
      // The first genuinely dual-corridor wave: you cannot spin to cover both.
      wave('w1', 3, [
        g('drone', N, { count: 6, fan: deg(30), gap: 0.4 }),
        g('drone', S, { count: 6, fan: deg(30), gap: 0.4 }),
      ]),
      wave('w2', 4, [
        g('asteroid', NW, { count: 2, fan: deg(20), gap: 1.2 }),
        g('drone', SE, { count: 9, fan: deg(44), at: 2, gap: 0.32 }),
      ]),
      wave('w3', 4, [
        g('drone', E, { count: 10, fan: deg(50), gap: 0.3 }),
        g('drone', W, { count: 10, fan: deg(50), at: 1.5, gap: 0.3 }),
      ]),
      wave('w4', 6, [
        g('asteroid', 'random', { count: 4, fan: deg(90), gap: 1.1 }),
        g('drone', 'random', { count: 12, fan: deg(70), at: 4, gap: 0.28 }),
      ]),
    ],
    ['inner-reach-3'],
    [],
    // The crust arrives in level two: same board, plus mounts that hold a
    // bearing instead of sweeping past it.
    {
      upgrades: ['hull', 'magnet', 'servos'],
      mounts: { surface: 4, ring: 2 },
      startingUpgrades: { magnet: 1 },
    },
  ),
  level(
    'inner-reach-3',
    'Weight',
    'standard',
    R,
    28,
    [
      wave('w1', 4, [g('asteroid', S, { count: 4, fan: deg(34), gap: 1.0 })]),
      wave('w2', 4, [
        g('asteroid', N, { count: 3, fan: deg(26), gap: 1.1 }),
        g('drone', S, { count: 8, fan: deg(40), at: 2.5, gap: 0.34 }),
      ]),
      wave('w3', 5, [g('asteroid', 'random', { count: 6, fan: deg(110), gap: 0.9 })]),
      wave('w4', 6, [
        g('asteroid', NE, { count: 4, fan: deg(30), gap: 0.9 }),
        g('asteroid', SW, { count: 4, fan: deg(30), at: 2, gap: 0.9 }),
        g('drone', 'random', { count: 10, fan: deg(60), at: 6, gap: 0.3 }),
      ]),
      wave('w5', 8, [
        g('asteroid', 'random', { count: 8, fan: deg(140), gap: 0.75 }),
        g('drone', 'random', { count: 14, fan: deg(80), at: 5, gap: 0.26 }),
      ]),
    ],
    ['inner-reach-4'],
    [],
    // Stations arrive here, two of them, on the bearings this level leans on.
    // Asteroids are slow and heavy and want a gun that is reliably pointed at
    // them — which is exactly what the orbit can't promise.
    { stations: stations(N, S) },
  ),
  level(
    'inner-reach-4',
    'Underneath',
    'standard',
    R,
    30,
    [
      // Burrowers dive under the ring. Everything on the orbit layer goes blind.
      wave('w1', 4, [g('burrower', N, { count: 3, fan: deg(28), gap: 0.9 })]),
      wave('w2', 5, [
        g('burrower', S, { count: 4, fan: deg(34), gap: 0.8 }),
        g('drone', N, { count: 8, fan: deg(40), at: 2, gap: 0.34 }),
      ]),
      wave('w3', 6, [
        g('burrower', 'random', { count: 6, fan: deg(120), gap: 0.7 }),
        g('asteroid', 'random', { count: 3, fan: deg(50), at: 3, gap: 1.0 }),
      ]),
      wave('w4', 7, [
        g('burrower', E, { count: 5, fan: deg(36), gap: 0.6 }),
        g('burrower', W, { count: 5, fan: deg(36), at: 1.2, gap: 0.6 }),
        g('drone', 'random', { count: 12, fan: deg(70), at: 5, gap: 0.28 }),
      ]),
      wave('w5', 9, [
        g('burrower', 'random', { count: 8, fan: deg(150), gap: 0.55 }),
        g('asteroid', 'random', { count: 5, fan: deg(90), at: 4, gap: 0.9 }),
      ]),
    ],
    ['inner-reach-5', 'inner-reach-6'],
    [],
    { stations: stations(E, W) },
  ),
  level(
    'inner-reach-5',
    'Freewheel',
    'elite',
    R,
    36,
    [
      wave('w1', 5, [
        g('drone', N, { count: 8, fan: deg(38), gap: 0.36 }),
        g('drone', S, { count: 8, fan: deg(38), gap: 0.36 }),
      ]),
      wave('w2', 6, [
        g('asteroid', 'random', { count: 5, fan: deg(120), gap: 0.9 }),
        g('burrower', 'random', { count: 4, fan: deg(80), at: 3, gap: 0.7 }),
      ]),
      wave('w3', 7, [
        g('drone', 'random', { count: 16, fan: deg(160), gap: 0.24 }),
        g('asteroid', 'random', { count: 4, fan: deg(70), at: 5, gap: 1.0 }),
      ]),
      wave('w4', 10, [
        g('burrower', 'random', { count: 8, fan: deg(180), gap: 0.5 }),
        g('drone', 'random', { count: 18, fan: deg(180), at: 4, gap: 0.22 }),
      ]),
    ],
    ['inner-reach-7'],
    // The orbit drags. Guns on the ring come round less often, so more of the
    // answer has to be solved on the crust and on the platforms.
    ['slow-orbit'],
    { stations: stations(N, E, S, W) },
  ),
  level(
    'inner-reach-6',
    'Flank',
    'standard',
    R,
    34,
    [
      // Cruisers hold a shield facing the planet — you have to hit them sideways.
      wave('w1', 4, [g('cruiser', N, { count: 2, fan: deg(24), gap: 1.4 })]),
      wave('w2', 5, [
        g('cruiser', SE, { count: 3, fan: deg(30), gap: 1.2 }),
        g('drone', NW, { count: 8, fan: deg(40), at: 3, gap: 0.34 }),
      ]),
      wave('w3', 6, [
        g('cruiser', E, { count: 3, fan: deg(26), gap: 1.1 }),
        g('cruiser', W, { count: 3, fan: deg(26), at: 2, gap: 1.1 }),
      ]),
      wave('w4', 8, [
        g('cruiser', 'random', { count: 5, fan: deg(130), gap: 1.0 }),
        g('burrower', 'random', { count: 5, fan: deg(90), at: 4, gap: 0.6 }),
      ]),
      wave('w5', 10, [
        g('cruiser', 'random', { count: 6, fan: deg(160), gap: 0.9 }),
        g('asteroid', 'random', { count: 5, fan: deg(110), at: 4, gap: 0.9 }),
        g('drone', 'random', { count: 12, fan: deg(80), at: 8, gap: 0.26 }),
      ]),
    ],
    ['inner-reach-7'],
  ),
  level('inner-reach-7', 'Derelict Cache', 'cache', R, 0, [], ['inner-reach-8']),
  level(
    'inner-reach-8',
    'The Lie',
    'standard',
    R,
    38,
    [
      // Feints exist to stop the game solving itself. This is where they land.
      wave('w1', 5, [g('feint', S, { count: 3, fan: deg(24), gap: 1.0 })]),
      wave('w2', 6, [
        g('feint', N, { count: 4, fan: deg(30), gap: 0.8 }),
        g('drone', S, { count: 8, fan: deg(40), at: 2.5, gap: 0.32 }),
      ]),
      wave('w3', 8, [
        g('feint', 'random', { count: 5, fan: deg(120), gap: 0.8 }),
        g('cruiser', 'random', { count: 3, fan: deg(60), at: 3, gap: 1.1 }),
      ]),
      wave('w4', 9, [
        g('feint', E, { count: 4, fan: deg(28), gap: 0.7 }),
        g('feint', W, { count: 4, fan: deg(28), at: 1.5, gap: 0.7 }),
        g('burrower', 'random', { count: 5, fan: deg(100), at: 5, gap: 0.7 }),
      ]),
      wave('w5', 12, [
        g('feint', 'random', { count: 8, fan: deg(180), gap: 0.55 }),
        g('burrower', 'random', { count: 6, fan: deg(140), at: 4, gap: 0.6 }),
        g('drone', 'random', { count: 14, fan: deg(100), at: 8, gap: 0.24 }),
      ]),
    ],
    ['inner-reach-9'],
  ),
  level(
    'inner-reach-9',
    'World Eater',
    'boss',
    R,
    44,
    [
      wave('w1', 8, [
        g('cruiser', 'random', { count: 4, fan: deg(140), gap: 1.0 }),
        g('drone', 'random', { count: 14, fan: deg(120), at: 4, gap: 0.24 }),
      ]),
      wave('w2', 9, [
        g('feint', 'random', { count: 6, fan: deg(160), gap: 0.6 }),
        g('burrower', 'random', { count: 6, fan: deg(140), at: 4, gap: 0.6 }),
      ]),
      wave('w3', 27, [
        g('world-eater', N, { count: 1, stage: 6 }),
        g('drone', 'random', { count: 12, fan: deg(180), at: 9, gap: 0.3 }),
        g('burrower', 'random', { count: 8, fan: deg(180), at: 18, gap: 0.5 }),
        g('drone', 'random', { count: 14, fan: deg(180), at: 28, gap: 0.26 }),
      ]),
    ],
    [],
  ),
];

/* ------------------------------------------------------------------ *
 * The other five regions.
 *
 * Composed from each region's roster on a shared difficulty curve rather than
 * placed wave by wave. That's a deliberate trade: it keeps every region
 * playable and consistently paced now, and the shape of the curve lives in one
 * function where it can be retuned in a single edit. Signature levels get
 * hand-authored the moment there's a wave editor to do it with (risk #2).
 * ------------------------------------------------------------------ */

const LEVEL_NAMES = [
  'Arrival',
  'Pressure',
  'The Narrows',
  'Hard Vacuum',
  'Nowhere To Spin',
  'Long Shadow',
  'Salvage',
  'Deep Cut',
];

/** A fixed bearing wheel, so composition stays deterministic across builds. */
const BEARINGS = [N, S, E, W, NE, SW, SE, NW];

/** Things that arrive in ones and twos rather than in fans. */
const HEAVY = new Set([
  'slagback',
  'rime-drifter',
  'cruiser',
  'carrier',
  'husk',
  'scrapper',
  'asteroid',
]);

function composeWave(
  region: RegionDef,
  levelIndex: number,
  waveIndex: number,
  seed: number,
): WaveDef {
  const groups: WaveGroup[] = [];
  const difficulty = levelIndex + waveIndex * 0.45;
  // Two groups early, three once the region has had time to teach itself.
  const groupCount = waveIndex >= 2 && levelIndex >= 2 ? 3 : 2;

  for (let i = 0; i < groupCount; i++) {
    const enemy = region.roster[(seed + i * 3 + waveIndex * 5) % region.roster.length] as EnemyTypeId;
    // Early waves come in on a named bearing so the read is teachable; later
    // ones go anywhere.
    const bearing =
      i === 0 && waveIndex < 2 ? BEARINGS[(seed + waveIndex) % BEARINGS.length]! : ('random' as const);

    const heavy = HEAVY.has(enemy);
    const count = heavy
      ? Math.min(2 + Math.floor(difficulty * 0.5), 7)
      : Math.min(5 + Math.floor(difficulty * 1.4), 18);

    groups.push(
      g(enemy, bearing, {
        count,
        fan: deg(heavy ? 30 + difficulty * 8 : 40 + difficulty * 12),
        at: i * (2.5 + waveIndex * 0.4),
        gap: heavy ? 1.1 : Math.max(0.22, 0.5 - difficulty * 0.03),
      }),
    );
  }

  return wave(`w${waveIndex + 1}`, 4 + Math.round(difficulty), groups);
}

/** N platforms spaced evenly, rotated so each region has its own arrangement. */
function stationRing(count: number, regionIndex: number): readonly StationSpec[] {
  const offset = deg(18 * regionIndex);
  return stations(
    ...Array.from({ length: count }, (_, i) => (i / count) * Math.PI * 2 + offset),
  );
}

function buildRegion(region: RegionDef, regionIndex: number): LevelDef[] {
  const out: LevelDef[] = [];
  const count = 9;

  for (let i = 0; i < count; i++) {
    const n = i + 1;
    const id = `${region.id}-${n}`;
    const next = n < count ? [`${region.id}-${n + 1}`] : [];

    if (n === 7) {
      out.push(level(id, 'Derelict Cache', 'cache', region.id, 0, [], next));
      continue;
    }

    const isBoss = n === count;
    const isElite = n === 5;
    const waveCount = Math.min(3 + Math.floor(n / 2), 6);
    const waves: WaveDef[] = [];

    for (let w = 0; w < waveCount; w++) {
      waves.push(composeWave(region, i, w, regionIndex * 7 + i * 3 + w));
    }

    if (isBoss) {
      waves.push(
        wave('boss', 28, [
          g(region.boss, N, { count: 1, stage: 6 }),
          g(region.roster[0]!, 'random', { count: 12, fan: deg(180), at: 10, gap: 0.3 }),
          g(region.roster[1]!, 'random', { count: 8, fan: deg(180), at: 22, gap: 0.6 }),
        ]),
      );
    }

    out.push(
      level(
        id,
        isBoss ? 'The Host' : (LEVEL_NAMES[i] ?? `Sector ${n}`),
        isBoss ? 'boss' : isElite ? 'elite' : 'standard',
        region.id,
        24 + i * 3,
        waves,
        next,
        isElite ? [regionIndex % 2 === 0 ? 'slow-orbit' : 'double-burrowers'] : [],
        // Platforms from the third node on, offset per region so no two
        // regions present the same fixed bearings. Bosses get a fourth.
        n < 3 ? {} : { stations: stationRing(isBoss ? 4 : 3, regionIndex) },
      ),
    );
  }

  return out;
}

export const LEVELS: readonly LevelDef[] = [
  ...innerReach,
  ...REGIONS.filter((r) => r.id !== 'inner-reach').flatMap((r, i) => buildRegion(r, i + 1)),
];

export function levelsForRegion(regionId: string): LevelDef[] {
  return LEVELS.filter((l) => l.region === regionId);
}

/** Which level introduces each system — used by the tutorial gating (risk #4). */
export const UNLOCKS: Record<string, string> = {
  'inner-reach-1': 'build',
  'inner-reach-2': 'crust',
  'inner-reach-3': 'ultimate',
  'inner-reach-4': 'upgrade',
};
